import React from 'react';
import { useBrowserExtensionDetected } from '../../../apis/workspace-registry';
import { Image } from '../../../../ui/Image';
import { Button } from '../../../components/Button';
import { FeaturesList } from './FeaturesList';
import { InstallSteps } from './InstallSteps';
import { ExtensionDetectedSuccess } from './ExtensionDetectedSuccess';

import { useTranslation } from 'react-i18next';

interface ManualDownloadSetupProps {
  onComplete: () => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

export const ManualDownloadSetup: React.FC<ManualDownloadSetupProps> = ({
  onComplete,
  onCancel,
  isLoading = false,
}) => {
  const { t } = useTranslation('pleri/onboarding');
  const [isInstalling, setIsInstalling] = React.useState(false);
  const [showInstallSteps, setShowInstallSteps] = React.useState(false);
  const {
    data: isExtensionDetected,
    isLoading: isCheckingExtension,
    refetch,
  } = useBrowserExtensionDetected();
  const version = '1.0.12';
  const extensionUrl = `${window.location.origin}/extensions/search/${version}/pleri-latest-chrome.zip`;

  const handleInstall = async () => {
    setIsInstalling(true);
    setShowInstallSteps(true);
    setIsInstalling(false);
  };
  const handleDownload = () => {
    window.open(extensionUrl, '_blank');
  };
  const handleComplete = async () => {
    try {
      await onComplete();
    } catch (error) {
      console.error('Failed to complete browser extension setup:', error);
    }
  };
  const handleCheckInstallation = async () => {
    try {
      await refetch();
      await handleComplete();
    } catch (error) {
      alert(
        'An error occurred while checking the extension installation. Please try again.',
      );
    }
  };
  const isDisabled = isInstalling || isLoading || isCheckingExtension;
  if (isExtensionDetected && !showInstallSteps) {
    return (
      <ExtensionDetectedSuccess
        onReconfigure={() => setShowInstallSteps(true)}
        showReconfigure
      />
    );
  }
  return (
    <div className="mx-auto max-w-2xl">
      {/* Heading */}
      <div className="mb-8 text-center">
        <h2 className="text-2xl leading-tight font-semibold text-gray-900 dark:text-gray-100">
          {t('chromeExtension')}
        </h2>
      </div>
      <div className="rounded-lg bg-white p-6 dark:bg-gray-900">
        {/* Centered Icon */}
        <div className="mb-6 text-center">
          <div className="relative mx-auto inline-block">
            <div className="absolute inset-0 animate-[pulse_4s_ease-in-out_infinite] rounded-xl bg-[#FF732E]/10 blur-xl" />
            <Image
              name={'chrome-extension'}
              type={'resourceType'}
              className="relative h-20 w-20 rounded-xl object-cover"
              alt={'chrome-extension'}
            />
          </div>

          <div className="mt-4">
            <p className="text-gray-600 dark:text-gray-400">
              {t('whetherWeAreInAwsAzureGcp')}
            </p>
          </div>
        </div>

        {!showInstallSteps ? (
          <>
            {/* Learn More Section */}
            <FeaturesList />

            {/* Action Buttons */}
            <div className="flex flex-col items-center gap-4">
              <Button
                onClick={handleInstall}
                disabled={isDisabled}
                variant="primary"
                className="group relative flex w-full items-center justify-center rounded-lg px-8 py-3 text-sm font-medium transition-all"
              >
                {isDisabled ? (
                  <>
                    <span className="opacity-0">{t('installExtension')}</span>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    </div>
                  </>
                ) : (
                  <>
                    <span>{t('installExtension')}</span>
                  </>
                )}
              </Button>
              <button
                onClick={onCancel}
                disabled={isLoading}
                className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
              >
                {t('common:cancel')}
              </button>
            </div>
          </>
        ) : (
          <>
            <InstallSteps onDownload={handleDownload} />
            <div className="flex flex-col gap-4">
              <Button
                onClick={handleCheckInstallation}
                disabled={isDisabled}
                variant="primary"
                className="group relative flex w-full items-center justify-center rounded-lg px-8 py-3 text-sm font-medium transition-all"
              >
                {isDisabled ? (
                  <>
                    <span className="opacity-0">
                      {t('iHaveInstalledTheExtension')}
                    </span>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    </div>
                  </>
                ) : (
                  <>
                    <span>{t('iHaveInstalledTheExtension')}</span>
                  </>
                )}
              </Button>
              <button
                onClick={() => setShowInstallSteps(false)}
                className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
              >
                {t('backToFeatures')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
